import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import ReviewBadges from "@/components/ReviewBadges";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const SITE_URL = "https://tradeoff.blueshuckconsulting.com";

// TradeOff's side of every row — shown once, reused across all comparisons.
const ROWS = [
  { label: "Best for", tradeoff: "UK subcontractors & small trade firms" },
  { label: "Platform", tradeoff: "Any browser — nothing to install" },
  { label: "AI takeoff", tradeoff: "Built-in: detects scale, reads schedules & legends, auto-counts symbols" },
  { label: "Price the takeoff", tradeoff: "One-click AI pricing from your own rate library" },
  { label: "CIS & VAT reverse charge", tradeoff: "Automatic on Applications for Payment" },
  { label: "Progress billing", tradeoff: "Schedule-of-values billing & Applications for Payment" },
  { label: "Xero accounting sync", tradeoff: "One-click draft invoices, CIS & reverse charge handled" },
  { label: "Get paid online", tradeoff: "Clients pay by card (Stripe) or BACS" },
  { label: "Pricing", tradeoff: "Free plan, then £29–£79/month" },
  { label: "Free plan", tradeoff: "Yes — no card required" },
];

const COMPETITORS = {
  planswift: {
    name: "PlanSwift",
    metaTitle: "TradeOff vs PlanSwift — AI Takeoff & Estimating for UK Subcontractors",
    metaDesc: "TradeOff vs PlanSwift: a browser-based, AI takeoff and estimating alternative with UK CIS & VAT reverse charge, progress billing and Xero sync built in. Start free.",
    tagline: "A modern, browser-based alternative to PlanSwift — with AI takeoff and UK billing built in.",
    intro: "PlanSwift is a well-established Windows desktop tool for 2D digital takeoff, popular with small and mid-sized contractors and known for its Excel integration. It's a capable measuring tool — but it runs on Windows, is licensed per machine, and stops at takeoff. TradeOff runs in any browser, adds an AI takeoff assistant, and carries you all the way through to a priced estimate, UK-compliant progress billing and getting paid.",
    theirs: {
      "Best for": "2D takeoff for small–mid contractors",
      "Platform": "Windows desktop (installed per machine)",
      "AI takeoff": "Manual 2D takeoff; no built-in AI schedule reading",
      "Price the takeoff": "Export to Excel and build up costs there",
      "CIS & VAT reverse charge": "Not built in",
      "Progress billing": "Not built in",
      "Xero accounting sync": "Not built in",
      "Get paid online": "Not built in",
      "Pricing": "Reported ~£1,749/year per licence",
      "Free plan": "Free trial only",
    },
    faqs: [
      { q: "Is TradeOff a good PlanSwift alternative for UK subcontractors?", a: "Yes. If you want takeoff plus UK-ready estimating and progress billing (CIS, VAT reverse charge, Applications for Payment) in one browser-based tool — with AI to speed up quantities — TradeOff covers the whole job rather than just the takeoff step." },
      { q: "Does TradeOff run on Mac?", a: "Yes. TradeOff runs in any modern browser on Windows, Mac, tablet or phone — there's nothing to install, unlike PlanSwift's Windows desktop app." },
    ],
  },
  countfire: {
    name: "Countfire",
    metaTitle: "TradeOff vs Countfire — AI Takeoff, Estimating & Billing for UK Subcontractors",
    metaDesc: "TradeOff vs Countfire: automated AI takeoff plus UK estimating, CIS & VAT reverse charge, progress billing and Xero sync in one low-cost, browser-based tool. Start free.",
    tagline: "Automated takeoff like Countfire — plus estimating, UK billing and payments in one place.",
    intro: "Countfire is a cloud takeoff tool best known for automated symbol counting, popular with electrical estimators. It's strong at counting — but it's specialist and priced for larger estimating teams, and it stops at the takeoff. TradeOff also auto-counts symbols and reads schedules with AI, then takes you through to a priced estimate, CIS/VAT-ready progress billing and payment — at a subcontractor-friendly price.",
    theirs: {
      "Best for": "Automated counting for electrical estimators",
      "Platform": "Cloud (browser)",
      "AI takeoff": "Automated symbol counting & measurement",
      "Price the takeoff": "Assembly/rate mapping to cost codes",
      "CIS & VAT reverse charge": "Not built in",
      "Progress billing": "Not built in",
      "Xero accounting sync": "Not built in",
      "Get paid online": "Not built in",
      "Pricing": "No public pricing (reported £200–£400/user/mo)",
      "Free plan": "Demo only",
    },
    faqs: [
      { q: "Does TradeOff auto-count symbols like Countfire?", a: "Yes. Box one socket, door or light and TradeOff finds every matching symbol on the sheet, and its 'Detect all symbols' tool finds repeated symbols automatically. It also reads schedule and legend tables with AI." },
      { q: "Is TradeOff only for electricians?", a: "No. Countfire is aimed at electrical estimating; TradeOff is built for all UK subcontract trades — from groundworks and brickwork to M&E, drylining, flooring and fit-out." },
    ],
  },
  kreo: {
    name: "Kreo",
    metaTitle: "TradeOff vs Kreo — AI Takeoff, Estimating & UK Billing for Subcontractors",
    metaDesc: "TradeOff vs Kreo: AI takeoff and estimating built for UK subcontractors, with CIS & VAT reverse charge, progress billing, Xero sync and online payments. Start free.",
    tagline: "AI takeoff like Kreo — purpose-built for UK subcontractors and how they get paid.",
    intro: "Kreo is a capable London-based, cloud AI takeoff and estimating platform aimed at quantity surveyors and estimators, with automated measurement from PDF and CAD drawings. It's a strong general takeoff/estimating tool — but it stops at takeoff, estimating and reports. TradeOff also does AI takeoff and pricing, and then carries you through the parts a subcontractor actually gets paid on: CIS and VAT reverse charge, Applications for Payment, Xero sync and card/BACS payments — with a permanent free plan, not just a trial.",
    theirs: {
      "Best for": "QSs & estimators (general takeoff/estimating)",
      "Platform": "Cloud (browser, Windows/Mac)",
      "AI takeoff": "AI auto-measure/count from PDF & CAD drawings",
      "Price the takeoff": "No-code items & assemblies, dynamic reports",
      "CIS & VAT reverse charge": "Not built in",
      "Progress billing": "Not built in",
      "Xero accounting sync": "Excel export (no direct Xero sync)",
      "Get paid online": "Not built in",
      "Pricing": "Published subscription plans",
      "Free plan": "7-day free trial",
    },
    faqs: [
      { q: "Is TradeOff a good Kreo alternative for subcontractors?", a: "Yes. Kreo is excellent general AI takeoff and estimating for QSs. TradeOff is built specifically for UK subcontractors — the same AI-assisted takeoff and pricing, plus CIS, VAT reverse charge, Applications for Payment, Xero sync and getting paid, on a permanent free plan." },
      { q: "Does TradeOff use AI to measure drawings like Kreo?", a: "Yes. TradeOff detects the drawing scale, reads schedules and legends, and auto-counts symbols with AI, then prices the takeoff from your own rate library." },
    ],
  },
  costx: {
    name: "CostX",
    metaTitle: "TradeOff vs CostX (RIB) — Affordable AI Takeoff & Estimating for UK Subcontractors",
    metaDesc: "TradeOff vs RIB CostX: a low-cost, browser-based AI takeoff and estimating alternative for UK subcontractors, with CIS & VAT reverse charge, progress billing and Xero sync. Start free.",
    tagline: "The enterprise power of CostX isn't for everyone — TradeOff is built for subcontractors.",
    intro: "RIB CostX is a powerful enterprise estimating platform for large QS firms, with 2D and 3D/BIM quantity extraction and live-linked workbooks. It's superb — and priced and scoped accordingly, with custom enterprise licensing often in the thousands per year. Most subcontractors don't need BIM workbooks; they need fast takeoff, a priced estimate and UK-ready billing. That's exactly what TradeOff does, in the browser, at a fraction of the cost.",
    theirs: {
      "Best for": "Enterprise QS firms & BIM/3D estimating",
      "Platform": "Windows desktop / enterprise",
      "AI takeoff": "2D & 3D/BIM quantity extraction (advanced, complex)",
      "Price the takeoff": "Live-linked workbooks & rate libraries",
      "CIS & VAT reverse charge": "General estimating; not UK-subcontractor billing",
      "Progress billing": "Enterprise valuations (heavyweight)",
      "Xero accounting sync": "Enterprise integrations",
      "Get paid online": "Not built in",
      "Pricing": "Custom enterprise (reported £5,000–£15,000+/yr)",
      "Free plan": "No",
    },
    faqs: [
      { q: "Is TradeOff a cheaper alternative to CostX?", a: "Yes — TradeOff starts with a free plan and paid plans are £29–£79/month, versus CostX's custom enterprise pricing (reported in the thousands per year). TradeOff focuses on what subcontractors need rather than full BIM estimating." },
      { q: "Do I need BIM/3D takeoff?", a: "Most subcontractors don't. TradeOff does fast, accurate 2D takeoff from PDF and scanned drawings with AI assistance — ideal for day-to-day subcontract pricing without the complexity or cost of a BIM platform." },
    ],
  },
};

const Comparison = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const data = COMPETITORS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: data.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <div className="bg-white">
      <Seo title={data.metaTitle} description={data.metaDesc} path={`/vs/${slug}`} jsonLd={jsonLd} />
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Comparison</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.08]">
            TradeOff <span className="text-slate-400 font-normal">vs</span> <span className="text-blue-600">{data.name}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">{data.tagline}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="vs-hero-cta" className="bg-blue-600 hover:bg-blue-700 h-12 px-7 text-base">
              Start free <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/pricing")} className="h-12 px-7 text-base border-slate-300">See pricing</Button>
          </div>
          <p className="mt-4 text-sm text-slate-500">No credit card required · Free 1 project trial</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-slate-700 text-lg leading-relaxed">{data.intro}</p>
        </div>
      </section>

      {/* Comparison table */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm" data-testid="vs-table">
            <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200">
              <div className="p-4 text-sm font-semibold text-slate-500">&nbsp;</div>
              <div className="p-4 text-center font-display font-bold text-blue-600 border-l border-slate-200">TradeOff</div>
              <div className="p-4 text-center font-display font-bold text-slate-700 border-l border-slate-200">{data.name}</div>
            </div>
            {ROWS.map((row, i) => (
              <div key={row.label} className={`grid grid-cols-3 text-sm ${i % 2 ? "bg-white" : "bg-slate-50/40"} border-b border-slate-100 last:border-0`}>
                <div className="p-4 font-medium text-slate-700">{row.label}</div>
                <div className="p-4 border-l border-slate-100 flex gap-2 text-slate-800">
                  <Check className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                  <span>{row.tradeoff}</span>
                </div>
                <div className="p-4 border-l border-slate-100 text-slate-500">{data.theirs[row.label]}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-400">{data.name} details reflect our understanding of publicly available information as of 2026 and may change — always check {data.name}'s own website for the latest. Pricing shown is indicative.</p>
        </div>
      </section>

      {/* Why TradeOff */}
      <section className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 text-center">Why UK subcontractors choose TradeOff</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { t: "One tool, whole workflow", d: "Takeoff, estimate, bill and get paid — no juggling a takeoff app plus spreadsheets plus your accountant." },
              { t: "AI does the grunt work", d: "Detect scale, read schedules and auto-count symbols in one pass, then price from your own rate library." },
              { t: "UK tax built in", d: "CIS deductions and VAT domestic reverse charge handled automatically on your Applications for Payment." },
              { t: "Works anywhere", d: "Any browser on Windows, Mac, tablet or phone — snap a drawing on site and get quantities back." },
              { t: "Fair, honest pricing", d: "Start free, then £29–£79/month. Pay for active projects, not per seat." },
              { t: "Xero in one click", d: "Send any valuation to Xero as a draft invoice with CIS and reverse charge handled." },
            ].map((c) => (
              <div key={c.t} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center"><Sparkles className="h-5 w-5 text-blue-600" /></div>
                <h3 className="mt-4 font-display font-semibold text-slate-900">{c.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <ReviewBadges />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-slate-900 text-center">TradeOff vs {data.name} — FAQs</h2>
          <Accordion type="single" collapsible className="mt-10">
            {data.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`vs-faq-${i}`}>
                <AccordionTrigger className="text-left text-base font-semibold text-slate-800">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-[15px] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: "#16305C" }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">See why subcontractors switch to TradeOff</h2>
          <p className="mt-4 text-slate-300">Start free today — takeoff, estimate and bill your next job in one place.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="vs-cta-btn" className="mt-8 bg-blue-600 hover:bg-blue-500 h-12 px-8 text-base">
            Create your workspace <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Comparison;
